/**
 * T478 — OCR-engine state surface.
 *
 * Frontend infers engine mode from a Vite env (`VITE_OCR_ENGINE` =
 * `production` | `mocked` | `disabled`). At deploy-time prod uses
 * `production`; tests + offline-dev use `mocked` so the UI greys out
 * OCR-dependent controls without hiding them.
 *
 * Per FR-013a × FR-020 the related `useOcrButtonState` hook combines
 * OCR-engine state AND KI-provider state so a button (e.g. "Mit KI
 * erkennen") shows the COMBINED reason in its tooltip when both are
 * unavailable.
 */
export type OcrEngineState = 'production' | 'mocked' | 'disabled';
export type KiProviderState = 'configured' | 'missing-token' | 'disabled';

export function useOcrEngineState(): OcrEngineState {
  // Vite injects import.meta.env at build-time. Default to 'production'
  // so prod builds without an explicit env behave correctly.
  const raw = (
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_OCR_ENGINE ?? 'production'
  ).toLowerCase();
  if (raw === 'mocked' || raw === 'disabled') return raw;
  return 'production';
}

export function useKiProviderState(): KiProviderState {
  const raw = (
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_KI_PROVIDER ?? 'configured'
  ).toLowerCase();
  if (raw === 'missing-token' || raw === 'disabled') return raw;
  return 'configured';
}

/**
 * Combined hook that returns the disabled-state + tooltip text for any
 * OCR-and-KI-dependent control (Konfidenz-Badge ranking, Re-Run, Mit KI
 * erkennen, etc.). FR-013a + FR-020.
 */
export function useOcrButtonState(): {
  disabled: boolean;
  tooltip: string;
  ocrEngine: OcrEngineState;
  kiProvider: KiProviderState;
} {
  const ocr = useOcrEngineState();
  const ki = useKiProviderState();
  const reasons: string[] = [];
  if (ocr !== 'production') reasons.push(`OCR ${ocr}`);
  if (ki !== 'configured') reasons.push(`KI ${ki}`);
  const disabled = reasons.length > 0;
  const tooltip = disabled
    ? `Derzeit nicht aktiv (${reasons.join(', ')}) — manuelle Erfassung erforderlich`
    : 'OCR + KI aktiv';
  return { disabled, tooltip, ocrEngine: ocr, kiProvider: ki };
}
